import { compact, isNil, last, upperFirst } from "lodash";
import AppContext from "PFApp/app_context";
import { useCustomValues } from "PFCore/hooks/queries/custom_values/use_custom_values";
import { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

type UseFormattedPreferencesReturn = {
  formattedWorkTypes: string | null;
  formattedProjectLength: string | null;
  formattedSkills: string | null;
  isLoading: boolean;
};

export const useFormattedPreferences = (): UseFormattedPreferencesReturn => {
  const { t } = useTranslation("marketplace", { keyPrefix: "dashboard.myPreferences" });
  const {
    store: {
      currentProfile: { preferences }
    }
  } = useContext(AppContext);

  const { data: skillsCustomValues, isFetching } = useCustomValues(
    {
      ids: preferences.skills
    },
    { enabled: Boolean(preferences.skills?.length) }
  );

  const enumerateItems = useCallback(
    (items: string[]): string => {
      const itemsWithoutLast = items.slice(0, -1).join(", ");
      const lastItem = last(items);

      return upperFirst(compact([itemsWithoutLast, lastItem]).join(` ${t("and")} `));
    },
    [t]
  );

  const workTypes: string[] = compact([
    preferences.office_work && t("workTypes.office"),
    preferences.remote_work && t("workTypes.remote"),
    preferences.hybrid_work && t("workTypes.hybrid")
  ]);

  const enumeratedWorkTypes = enumerateItems(workTypes);
  const formattedWorkTypes =
    workTypes.length > 0
      ? t("workTypes.typesInformation", {
          types: enumeratedWorkTypes,
          count: workTypes.length
        })
      : null;

  const isProjectLengthSet =
    !isNil(preferences.project_min_duration_months) && !isNil(preferences.project_max_duration_months);

  const formattedProjectLength = isProjectLengthSet
    ? t("monthsDuration", {
        min: preferences.project_min_duration_months,
        max: preferences.project_max_duration_months
      })
    : null;

  const skills = skillsCustomValues?.entries || [];
  const enumeratedSkills = enumerateItems(skills.map(({ text }) => text));
  const formattedSkills =
    skills.length > 0 ? t("skills", { skills: enumeratedSkills, count: skills.length }) : null;

  return {
    formattedWorkTypes,
    formattedProjectLength,
    formattedSkills,
    isLoading: isFetching
  };
};
